import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmailIcon from '@mui/icons-material/Email';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HomeIcon from '@mui/icons-material/Home';
import LinkIcon from '@mui/icons-material/Link';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Divider,
    FormControl,
    FormControlLabel,
    FormLabel,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import axios from 'axios';
import { MRT_ColumnDef, MaterialReactTable } from 'material-react-table';
import { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { api } from '../../apiConfig';
import {
    BillingInfo,
    BusinessInfo,
    ContactType,
    Facility,
    ShippingInfo,
} from './types';

interface Customer {
    id: number;
    status: string;
    businessInfo: BusinessInfo;
    contactInfo: { contacts: ContactType[] };
    billingInfo: BillingInfo;
    shippingInfo: ShippingInfo;
    facilities: Facility[];
}

const CustomerStatus = {
    Pending: 'Pending',
    Approved: 'Approved',
    Denied: 'Denied',
} as const;

const ApprovalPage = () => {
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingAction, setLoadingAction] = useState<{
        id: number;
        action: 'Approve' | 'Deny';
    } | null>(null);
    const [statusFilter, setStatusFilter] = useState<string[]>([]);
    const [billingValidationMap, setBillingValidationMap] = useState<Record<number, boolean>>({});
    const [proRateValidationMap, setProRateValidationMap] = useState<Record<number, boolean>>({});
    const [billingTracks, setBillingTracks] = useState<{ id: number; name: string }[]>([]);
    const [statusErrors, setStatusErrors] = useState<Record<number, string>>({});

    useEffect(() => {
        getCustomers();
        getBillingTracks();
    }, []);

    const getCustomers = async () => {
        setLoading(true);

        try {
            const response = await api.get(`/api/customers`);
            setCustomers(response.data);
        } catch (error) {
            console.error('Error fetching customers:', error);
            setCustomers([]);
        } finally {
            setLoading(false);
        }
    };

    const getBillingTracks = async () => {
        var response = await api.get(`/api/customers/billingtracks`);
        setBillingTracks(response.data);
    };

    const handleCustomerStatusChange = async (
        userId: number,
        statusName: string
    ) => {
        const isApproving = statusName === CustomerStatus.Approved;
        const customer = customers.find((c) => c.id === userId);

        if (isApproving) {
            let valid = true;

            if (!customer?.billingInfo?.billingTrack) {
                setBillingValidationMap((prev) => ({ ...prev, [userId]: true }));
                valid = false;
            } else {
                setBillingValidationMap((prev) => ({ ...prev, [userId]: false }));
            }

            if (customer?.billingInfo?.isProRate === null || customer?.billingInfo.isProRate === undefined) {
                setProRateValidationMap((prev) => ({ ...prev, [userId]: true }));
                valid = false;
            } else {
                setProRateValidationMap((prev) => ({ ...prev, [userId]: false }));
            }

            if (!valid) return;
        }

        setLoadingAction({
            id: userId,
            action: isApproving ? 'Approve' : 'Deny',
        });

        try {
            await api.put(`/api/customers/${userId}/status`,
                {
                    status: statusName,
                    billingTrackId: customer?.billingInfo?.billingTrack?.id ?? null,
                    prorateBilling: customer?.billingInfo?.isProRate ?? null
                },
                {
                    headers: { 'Content-Type': 'application/json' },
                }
            );

            setCustomers((prev) =>
                prev.map((customer) =>
                    customer.id === userId
                        ? { ...customer, status: statusName }
                        : customer
                )
            );

            setStatusErrors((prev) => {
                const { [userId]: _, ...rest } = prev;
                return rest;
            });

        } catch (error: any) {
            const errorMsg = axios.isAxiosError(error)
                ? typeof error.response?.data === 'string'
                    ? error.response.data
                    : error.response?.data?.message || 'Unknown error from server.'
                : 'Unexpected error occurred.';

            setStatusErrors((prev) => ({
                ...prev,
                [userId]: errorMsg,
            }));
        } finally {
            setLoadingAction(null);
        }
    };

    const rows = customers.map((customer) => ({
        id: customer.id,
        businessInfo: customer.businessInfo,
        contactInfo: customer.contactInfo,
        billingInfo: customer.billingInfo,
        shippingInfo: customer.shippingInfo,
        facilities: customer.facilities,
        status: customer.status,
    }));

    const filteredRows = useMemo(() => {
        const filtered =
            statusFilter.length === 0
                ? rows
                : rows.filter((row) => statusFilter.includes(row.status));
        return [...filtered].sort((a, b) => b.id - a.id);
    }, [rows, statusFilter]);

    const columns: MRT_ColumnDef<any>[] = [
        {
            accessorKey: 'businessInfo.facilityName',
            header: 'Facility Name',
        },
        {
            accessorKey: 'businessInfo.address',
            header: 'Address',
            Cell: ({ row }) => {
                const { businessInfo } = row.original;
                return [
                    businessInfo.address,
                    businessInfo.address2,
                    businessInfo.city,
                    businessInfo.state,
                    businessInfo.zip,
                ]
                    .filter((part) => part && part.trim() !== '')
                    .join(', ');
            },
        },
    ];

    const formatPhoneNumber = (phone: string | undefined) => {
        if (!phone) return 'N/A';
        const cleaned = phone.replace(/\D/g, '');
        const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
        if (match) {
            return `(${match[1]}) ${match[2]}-${match[3]}`;
        }
        return phone;
    };

    const renderContact = (contact: any, showTitle = true) => {
        return (
            <>
                {showTitle && (
                    <Typography sx={{ fontWeight: 'bold' }}>
                        {contact?.contactType ?? 'N/A'}
                    </Typography>
                )}
                {contact?.sameAsPrimary ? (
                    <Typography
                        sx={{ fontStyle: 'italic', color: 'gray', fontSize: '0.875rem' }}
                    >
                        Same as primary
                    </Typography>
                ) : (
                    <>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                            <PersonIcon fontSize='small' sx={{ mr: 1 }} />
                            {`${contact?.firstName ?? 'N/A'} ${contact?.lastName ?? ''}`}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                            <PhoneIcon fontSize='small' sx={{ mr: 1 }} />
                            {formatPhoneNumber(contact?.phone)}{' '}
                            {contact?.ext ? `ext. ${contact.ext}` : ''}
                            {contact?.preferredMethod?.toLowerCase() === 'phone' && (
                                <Tooltip title='Preferred'>
                                    <CheckCircleIcon
                                        fontSize='small'
                                        color='error'
                                        sx={{ ml: 1 }}
                                    />
                                </Tooltip>
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                            <EmailIcon fontSize='small' sx={{ mr: 1 }} />
                            {contact?.email ?? 'N/A'}
                            {contact?.preferredMethod?.toLowerCase() === 'email' && (
                                <Tooltip title='Preferred'>
                                    <CheckCircleIcon
                                        fontSize='small'
                                        color='error'
                                        sx={{ ml: 1 }}
                                    />
                                </Tooltip>
                            )}
                        </Box>
                    </>
                )}
            </>
        );
    };

    const renderAddress = (address: any, sameAsText?: string) => {
        const hasAddress =
            (address?.address && address.address.trim() !== '') ||
            (address?.address2 && address.address2.trim() !== '') ||
            (address?.city && address.city.trim() !== '') ||
            (address?.state && address.state.trim() !== '') ||
            (address?.zip && address.zip.trim() !== '');

        return (
            <>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}>
                    {hasAddress && (
                        <HomeIcon fontSize='small' sx={{ mr: 1, mt: 0.5 }} />
                    )}
                    <Box>
                        <div>{address?.address}</div>
                        {address?.address2 && address.address2.trim() !== '' && (
                            <div>{address.address2}</div>
                        )}
                        <div>
                            {[address?.city, address?.state, address?.zip]
                                .filter((part) => part && part.trim() !== '')
                                .join(', ')}
                        </div>
                    </Box>
                </Box>
                {sameAsText && (
                    <Typography
                        sx={{ fontStyle: 'italic', color: 'gray', fontSize: '0.875rem' }}
                    >
                        {sameAsText}
                    </Typography>
                )}
            </>
        );
    };

    const renderSameAsLabel = (text: string) => (
        <Typography
            sx={{
                fontStyle: 'italic',
                color: 'info.main',
                fontSize: '0.875rem',
                display: 'flex',
                alignItems: 'center',
                mt: 1,
            }}
        >
            <LinkIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
            {text}
        </Typography>
    );

    const renderDeliveryOption = (option?: { name: string }, other?: string) => {
        if (!option?.name) return null;
        const name = option.name.toLowerCase() === 'other' && other
            ? `Other — ${other}`
            : option.name;
        return (
            <Box sx={{ mt: 3, px: 2, py: 1, borderRadius: 999, backgroundColor: 'rgba(0, 0, 0, 0.04)', fontSize: '0.85rem', lineHeight: 1.4, display: 'inline-block', maxWidth: '100%', }}>
                <Box component='span' sx={{ fontWeight: 'bold' }}>
                    Delivery Instruction:
                </Box>
                <br />
                {name}
            </Box>
        );
    };


    return (
        <Stack sx={{ maxWidth: 1200, margin: 'auto', width: '100%', padding: 6 }}>
            <Typography variant='h5' mb={5} color={'grey'} fontWeight={'bold'}>
                Customer List
            </Typography>
            <Stack justifyContent='flex-end' direction='row'>
                {/*<Button>Legacy Customers</Button>*/}
            </Stack>
            {(customers === null || customers.length === 0) && !loading ? (
                <Typography align='center' color='gray'>
                    No customers found.
                </Typography>
            ) : (
                <>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                        <Autocomplete
                            multiple
                            options={[
                                CustomerStatus.Pending,
                                CustomerStatus.Approved,
                                CustomerStatus.Denied,
                            ]}
                            value={statusFilter}
                            onChange={(e, newValue) => setStatusFilter(newValue)}
                            renderInput={(params) => (
                                <TextField {...params} label='Filter by Status' />
                            )}
                            sx={{ width: 250 }}
                        />
                    </Box>

                    <MaterialReactTable
                        columns={columns}
                        data={filteredRows}
                        initialState={{density: 'compact'}}
                        muiTableBodyCellProps={{
                            sx: {
                                fontSize: '1rem',
                            },
                        }}
                        muiTableBodyRowProps={({ row }) => {
                            const status = row.original.status;
                            let backgroundColor = '';
                            if (status === CustomerStatus.Approved)
                                backgroundColor = '#d4edda';
                            else if (status === CustomerStatus.Denied)
                                backgroundColor = '#f8d7da';

                            return { sx: { backgroundColor } };
                        }}
                        renderDetailPanel={({ row }) => {
                            const {
                                businessInfo,
                                contactInfo,
                                billingInfo,
                                shippingInfo,
                                facilities,
                                id,
                                status,
                            } = row.original;
                            return (
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                                        gap: 2,
                                        border: '1px solid lightgrey',
                                        borderRadius: 2,
                                        padding: 2,
                                    }}
                                >
                                    {/* Business Information */}
                                    <Card>
                                        <CardContent>
                                            <Typography variant='h6'>Business Information</Typography>
                                            <Divider sx={{ my: 1 }} />
                                            <div>
                                                <strong>Name:</strong> {businessInfo?.facilityName}
                                            </div>
                                            {businessInfo?.businessType && (
                                                <div style={{ marginTop: '0.5rem' }}>
                                                    <strong>Type:</strong>{' '}
                                                    <span>{businessInfo.businessType.name}</span>
                                                    {businessInfo.otherBusinessType && (
                                                        <span
                                                            style={{
                                                                fontStyle: 'italic',
                                                                color: 'gray',
                                                                marginLeft: '0.5rem',
                                                            }}
                                                        >
                                                            — {businessInfo.otherBusinessType}
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                            <Box sx={{ mt: 2 }} />
                                            {renderAddress(businessInfo)}
                                            <Box
                                                sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}
                                            >
                                                <PhoneIcon fontSize='small' sx={{ mr: 1 }} />
                                                {formatPhoneNumber(businessInfo?.phone)}
                                            </Box>
                                        </CardContent>
                                    </Card>

                                    {/* Shipping Information */}
                                    <Card>
                                        <CardContent>
                                            <Typography variant='h6'>Shipping Information</Typography>
                                            <Divider sx={{ my: 1 }} />
                                            {renderAddress(shippingInfo)}
                                            {shippingInfo?.sameAsBusiness &&
                                                renderSameAsLabel('Same as business address')}

                                            {shippingInfo?.contact ? (
                                                <Box sx={{ mt: 3 }}>
                                                    {renderContact(shippingInfo.contact, false)}
                                                </Box>
                                            ) : (
                                                <>
                                                    <Box sx={{ mt: 2 }} />
                                                    <div>No shipping contact</div>
                                                </>
                                            )}

                                            {shippingInfo?.deliveryOption?.name &&
                                                renderDeliveryOption(shippingInfo?.deliveryOption, shippingInfo?.deliveryOptionOther)
                                            }
                                        </CardContent>
                                    </Card>

                                    {/* Billing Information */}
                                    <Card>
                                        <CardContent>
                                            <Typography variant='h6'>Billing Information</Typography>
                                            <Divider sx={{ my: 1 }} />
                                            {renderAddress(billingInfo)}
                                            {billingInfo?.sameAsShipping &&
                                                renderSameAsLabel('Same as shipping address')}
                                            {billingInfo?.contact ? (
                                                <Box sx={{ mt: 3 }}>
                                                    {renderContact(billingInfo.contact, false)}
                                                </Box>
                                            ) : (
                                                <>
                                                    <Box sx={{ mt: 2 }} />
                                                    <div>No billing contact</div>
                                                </>
                                            )}
                                        </CardContent>
                                    </Card>

                                    {/* Contacts */}
                                    <Card>
                                        <CardContent>
                                            <Typography variant='h6'>Contacts</Typography>
                                            <Divider sx={{ my: 1 }} />
                                            {contactInfo?.contacts?.length ? (
                                                contactInfo.contacts.map((c: ContactType, idx: number) => (
                                                    <Box key={idx} sx={{ mb: 2 }}>
                                                        {renderContact(c)}
                                                        {idx < contactInfo.contacts.length - 1 && (
                                                            <Divider sx={{ my: 2 }} />
                                                        )}
                                                    </Box>
                                                ))
                                            ) : (
                                                <Box sx={{ mt: 2.75 }}>
                                                    <div>No contacts available</div>
                                                </Box>
                                            )}
                                        </CardContent>
                                    </Card>

                                    {/* Facilities */}
                                    {facilities?.length > 0 && (
                                        <Box sx={{ gridColumn: '1 / -1', width: '100%' }}>
                                            {facilities.map((facility: Facility, index: number) => (
                                                <Accordion key={index} sx={{ mb: 2 }}>
                                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                        <Typography
                                                            sx={{
                                                                fontWeight: 'bold',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                            }}
                                                        >
                                                            <BusinessIcon sx={{ mr: 1 }} />
                                                            {facility.businessInfo.facilityName}
                                                        </Typography>
                                                    </AccordionSummary>
                                                    <AccordionDetails>
                                                        <Box
                                                            sx={{
                                                                display: 'grid',
                                                                gridTemplateColumns: 'repeat(3, 1fr)',
                                                                gap: 2,
                                                            }}
                                                        >
                                                            {/* Facility Info */}
                                                            <Card>
                                                                <CardContent>
                                                                    <Typography
                                                                        sx={{ fontWeight: 'bold', mb: 1 }}
                                                                    >
                                                                        Facility Information
                                                                    </Typography>
                                                                    {renderAddress(facility.businessInfo)}

                                                                    {facility.shippingInfo?.deliveryOption?.name &&
                                                                        renderDeliveryOption(facility.shippingInfo?.deliveryOption, facility.shippingInfo?.deliveryOptionOther)
                                                                    }
                                                                </CardContent>
                                                            </Card>

                                                            {/* Shipping Contact */}
                                                            <Card>
                                                                <CardContent>
                                                                    <Typography
                                                                        sx={{ fontWeight: 'bold', mb: 1 }}
                                                                    >
                                                                        Shipping Contact
                                                                    </Typography>
                                                                    {facility.shippingInfo?.contact ? (
                                                                        renderContact(
                                                                            facility.shippingInfo.contact,
                                                                            false
                                                                        )
                                                                    ) : (
                                                                        <Typography color='text.secondary'>
                                                                            No shipping contact available
                                                                        </Typography>
                                                                    )}
                                                                </CardContent>
                                                            </Card>

                                                            {/* Billing Info */}
                                                            <Card>
                                                                <CardContent>
                                                                    <Typography
                                                                        sx={{ fontWeight: 'bold', mb: 1 }}
                                                                    >
                                                                        Billing Information
                                                                    </Typography>

                                                                    {facility.billingInfo?.address?.trim() ? (
                                                                        renderAddress(facility.billingInfo)
                                                                    ) : (
                                                                        <>
                                                                            {renderAddress(businessInfo)}
                                                                            {renderSameAsLabel('Same as corporate address')}
                                                                        </>
                                                                    )}

                                                                    {facility.billingInfo?.contact && (
                                                                        <Box sx={{ mt: 2 }}>
                                                                            {renderContact(
                                                                                facility.billingInfo.contact,
                                                                                false
                                                                            )}
                                                                        </Box>
                                                                    )}
                                                                </CardContent>
                                                            </Card>
                                                        </Box>
                                                    </AccordionDetails>
                                                </Accordion>
                                            ))}
                                        </Box>
                                    )}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            mt: 2,
                                            width: '100%',
                                            gridColumn: '1 / -1',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 2,
                                                flexGrow: 1,
                                                minHeight: 56,
                                            }}
                                        >
                                            {status !== CustomerStatus.Approved && (
                                                <>
                                                    <Box>
                                                        <Select
                                                            placeholder="Billing Track *"
                                                            value={
                                                                billingInfo?.billingTrack
                                                                    ? {
                                                                        value: billingInfo?.billingTrack.id,
                                                                        label: billingInfo?.billingTrack.name,
                                                                    }
                                                                    : null
                                                            }
                                                            onChange={(option) => {
                                                                if (!option) return;
                                                                const selected = billingTracks.find((bt) => bt.id === option.value);
                                                                setCustomers((prev) =>
                                                                    prev.map((c) =>
                                                                        c.id === id
                                                                            ? {
                                                                                ...c,
                                                                                billingInfo: {
                                                                                    ...c.billingInfo,
                                                                                    billingTrack: selected ?? null,
                                                                                },
                                                                            }
                                                                            : c
                                                                    )
                                                                );
                                                                setBillingValidationMap((prev) => ({ ...prev, [id]: false }));
                                                            }}
                                                            options={billingTracks.map((bt) => ({
                                                                value: bt.id,
                                                                label: bt.name,
                                                            }))}
                                                            menuPortalTarget={document.body}
                                                            styles={{
                                                                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                                                control: (base) => ({
                                                                    ...base,
                                                                    borderColor: billingValidationMap[id] ? 'red' : base.borderColor,
                                                                    minWidth: 175,
                                                                }),
                                                            }}
                                                        />
                                                    </Box>

                                                    <FormControl
                                                        component='fieldset'
                                                        error={proRateValidationMap[id] ?? false}
                                                        sx={{ flexDirection: 'row', alignItems: 'center' }}
                                                    >
                                                        <FormLabel sx={{ mr: 2 }}>Pro Rate Billing</FormLabel>
                                                        <RadioGroup
                                                            row
                                                            name='isProRate'
                                                            value={
                                                                billingInfo?.isProRate === true
                                                                    ? 'true'
                                                                    : billingInfo?.isProRate === false
                                                                        ? 'false'
                                                                        : ''
                                                            }
                                                            onChange={(e) => {
                                                                const selected = e.target.value === 'true';
                                                                setCustomers((prev) =>
                                                                    prev.map((c) =>
                                                                        c.id === id
                                                                            ? {
                                                                                ...c,
                                                                                billingInfo: {
                                                                                    ...c.billingInfo,
                                                                                    isProRate: selected,
                                                                                },
                                                                            }
                                                                            : c
                                                                    )
                                                                );
                                                                setProRateValidationMap((prev) => ({ ...prev, [id]: false }));
                                                            }}
                                                        >
                                                            <FormControlLabel
                                                                value='true'
                                                                control={<Radio />}
                                                                label='Yes'
                                                            />
                                                            <FormControlLabel
                                                                value='false'
                                                                control={<Radio />}
                                                                label='No'
                                                            />
                                                        </RadioGroup>
                                                    </FormControl>
                                                </>
                                            )}
                                        </Box>

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'flex-end',
                                                gap: 1,
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                {status !== CustomerStatus.Approved && (
                                                    <Button
                                                        variant='contained'
                                                        color='success'
                                                        onMouseDown={() =>
                                                            handleCustomerStatusChange(id, CustomerStatus.Approved)
                                                        }
                                                        disabled={
                                                            loadingAction?.id === id &&
                                                            loadingAction?.action === 'Approve'
                                                        }
                                                        startIcon={
                                                            loadingAction?.id === id &&
                                                                loadingAction?.action === 'Approve' ? (
                                                                <CircularProgress size={20} />
                                                            ) : null
                                                        }
                                                    >
                                                        Approve
                                                    </Button>
                                                )}

                                                {status !== CustomerStatus.Denied && (
                                                    <Button
                                                        variant='contained'
                                                        color='error'
                                                        onClick={() =>
                                                            handleCustomerStatusChange(id, CustomerStatus.Denied)
                                                        }
                                                        disabled={
                                                            loadingAction?.id === id &&
                                                            loadingAction?.action === 'Deny'
                                                        }
                                                        startIcon={
                                                            loadingAction?.id === id &&
                                                                loadingAction?.action === 'Deny' ? (
                                                                <CircularProgress size={20} />
                                                            ) : null
                                                        }
                                                    >
                                                        Deny
                                                    </Button>
                                                )}
                                            </Box>

                                            {statusErrors[id] && (
                                                <Typography color="error" variant="body2" sx={{ mt: 1, fontWeight: 500 }} align="right">
                                                    {statusErrors[id]}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                            );
                        }}
                    />
                </>
            )}
        </Stack>
    );
};

export default ApprovalPage;
